import React from 'react';

/**
 * Shared input className for consistent form field styling
 */
export const inputClassName =
  'bg-white flex items-center gap-2 py-3 px-4 shrink-0 self-stretch rounded-sm border border-gray-60 text-base font-normal leading-6 text-gray-90 transition-colors duration-300 focus:outline-none focus:border-blue-50';

/**
 * @param {object} props
 * @param {string} props.label - Label text displayed above the input
 * @param {React.ReactNode} props.children - Input element(s) to wrap
 * @param {string} [props.className] - Additional CSS classes for the label wrapper
 * @param {boolean} [props.optional] - Shows "optional" text next to label
 */
export const FieldLabel = ({
  label,
  children,
  className = '',
  optional = false,
}) => (
  // biome-ignore lint/a11y/noLabelWithoutControl: `children` is the input element
  <label
    className={`flex flex-col items-start gap-1 self-stretch ${className}`}
  >
    <span
      className={`flex items-start gap-2.5 shrink-0 self-stretch text-sm font-semibold leading-5 text-gray-90 ${optional ? 'justify-between w-full' : ''}`}
    >
      <span>{label}</span>
      {optional && (
        <span className="text-sm font-extralight leading-5 text-gray-90 text-right">
          optional
        </span>
      )}
    </span>
    {children}
  </label>
);

export const ErrorMessage = ({ children = '' }) => {
  if (!children) return null;
  return (
    <p className="text-xs font-normal leading-4 text-coral-90 mt-1">
      {children}
    </p>
  );
};

/**
 * Text input field with label and error handling
 * @param {object} props
 * @param {string} props.label - Label text
 * @param {string} [props.type='text'] - Input type (text, email, tel, etc.)
 * @param {string} [props.placeholder] - Placeholder text
 * @param {object} [props.error] - RHF error object with message property
 * @param {object} props.registration - Spread from register()
 * @param {string} [props.className] - Additional wrapper classes
 * @param {boolean} [props.optional] - Shows "optional" indicator next to label
 */
export const TextField = ({
  label,
  type = 'text',
  placeholder,
  error,
  registration,
  className = '',
  optional = false,
}) => (
  <FieldLabel label={label} className={className} optional={optional}>
    <input
      type={type}
      placeholder={placeholder}
      className={inputClassName}
      {...registration}
    />
    <ErrorMessage>{error?.message}</ErrorMessage>
  </FieldLabel>
);
